import type { Maybe } from "@graphql-tools/utils";
import { t } from "@lingui/macro";
import { captureEvent } from "@sentry/react";
import {
  type AssignableFile,
  CapacityAllocationTypeEnum,
  EditTaskWatchersDocument,
  type EditTaskWatchersMutation,
  type EditTaskWatchersMutationVariables,
  type SimpleTaskForClientFragment,
  type SimpleTaskFragment,
  StarsStarableTypeEnum,
  type Task,
  type TaskComment,
  TaskDocument,
  TaskForScopedUsersDocument,
  type TaskForScopedUsersQuery,
  type TaskForScopedUsersQueryVariables,
  type TaskQuery,
  type TaskQueryVariables,
  type TaskWatcher,
  UpdateTaskDescriptionDocument,
  type UpdateTaskDescriptionMutation,
  type UpdateTaskDescriptionMutationVariables,
} from "@src/__generated__/graphql";
import type { IOption } from "@src/components/ui-kit";
import type { Attachment } from "@src/components/ui-kit/TextEditor/extensions/UploadAttachmentPlugin";
import { EMPTY_EDITOR_CONTENT } from "@src/components/ui-kit/TextEditor/TextEditor";
import { TASK_ID_QUERY_KEY } from "@src/components/widgets/Modals/ModalCommunication/CommunicationModalHeader";
import { CommunicationTabParent } from "@src/components/widgets/Modals/ModalCommunication/components/tabs/communication-tab-parent";
import { COMMUNICATION_TAB_QUERY_KEY } from "@src/components/widgets/Modals/ModalCommunication/components/tabs/communication-tab-query";
import { DescriptionTab } from "@src/components/widgets/Modals/ModalCommunication/components/tabs/DescriptionTab";
import { LinkedTasksTab } from "@src/components/widgets/Modals/ModalCommunication/components/tabs/LinkedTasksTab";
import { SubtasksTab } from "@src/components/widgets/Modals/ModalCommunication/components/tabs/SubtasksTab";
import { TaskAttachmentsTab } from "@src/components/widgets/Modals/ModalCommunication/components/tabs/TaskAttachmentsTab";
import type { TTaskTab } from "@src/components/widgets/Modals/ModalCommunication/components/tabs/TaskTab";
import { ModalCommunication } from "@src/components/widgets/Modals/ModalCommunication/ModalCommunication";
import { TaskModel } from "@src/components/widgets/Modals/ModalCommunication/models";
import { TaskTabIdEnum } from "@src/constants/tasks";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import { trackEvent } from "@src/services/amplitude";
import type { AppStore } from "@src/stores/AppStore";
import type { BaseStore } from "@src/stores/BaseStore";
import type { ModalStore } from "@src/stores/ModalStore";
import { can } from "@src/utils/components/permissions";
import { BooleanState } from "@src/utils/mobx/states/BooleanState";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { UnionState } from "@src/utils/mobx/states/UnionState";
import { ValueState } from "@src/utils/mobx/states/ValueState";
import type { GraphQLFormattedError } from "graphql";
import { debounce, omit } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import router from "next/router";
import type React from "react";
import { createRef } from "react";
import { CommunicationTabParentStore } from "./components/tabs/communication-tab-parent-store";

type DescriptionState = "write" | "read";
type RenderModeType = "drawer" | "modal";
type ModalCommunicationOptions = {
  id: Task["id"];
  allocationType?: CapacityAllocationTypeEnum;
  onChange?: (task: SimpleTaskFragment | SimpleTaskForClientFragment) => void;
  onDelete?: (task: TaskModel) => void;
  onDuplicate?: (task: SimpleTaskFragment) => void;
  onTimeTrackingItemCreated?: (trackedTime: number | undefined) => void;
};

const RENDER_MODE_CACHE_KEY = "task_detail_render_mode";
const DIVIDER_POSITION_CACHE_KEY = "task_detail_divider_position";

// TODO: rename store file to `TaskDetailModalStore` after Forecasting merge to avoid conflicts
export class TaskDetailModalStore implements BaseStore, ModalStore {
  appStore: AppStore;

  readonly modalId = "taskDetailModal";

  disclosureState = new DisclosureState<ModalCommunicationOptions>({
    onOpen: (additionalData) => {
      trackEvent("task", "Opening task drawer");
      this.appStore.UIStore.dialogs.openModal({
        id: this.modalId,
        content: <ModalCommunication />,
      });
      if (!additionalData?.id) return;
      this.taskId.set(additionalData?.id);
      this.descriptionState.set("read");
      this.communicationTabParentStore.reset();
      this.fetchTask();

      // Pre-fetch mentionable users for description tab (default: internal only)
      // and all communication tab scopes so mentions are instant when switching tabs
      this.appStore.workspaceStore.getDefaultMentionsUsers(
        ["internal"],
        additionalData?.id,
      );
      for (const tab of this.communicationTabParentStore.availableTabs) {
        this.appStore.workspaceStore.getDefaultMentionsUsers(
          tab.userTypesWithAccess,
          additionalData?.id,
        );
      }

      if (!router.query[TASK_ID_QUERY_KEY]) {
        router.replace(
          {
            pathname: router.pathname,
            query: {
              ...router.query,
              [TASK_ID_QUERY_KEY]: additionalData?.id,
            },
          },
          undefined,
          { shallow: true },
        );
      }

      // Add beforeunload listener to warn about unsaved changes
      this.addBeforeUnloadListener();
    },
    onClose: () => {
      // Remove beforeunload listener when closing modal
      this.removeBeforeUnloadListener();

      this.appStore.UIStore.dialogs.closeModal(this.modalId);
      this.handleChangeCommentInputValue("");
      this.handleChangeDescriptionInputValue(EMPTY_EDITOR_CONTENT);
      this.setDescriptionChanged(false);
      this.setCommentChanged(false);
      router.replace(
        {
          pathname: router.pathname,
          query: omit(
            router.query,
            TASK_ID_QUERY_KEY,
            COMMUNICATION_TAB_QUERY_KEY,
            "commentId",
          ),
        },
        undefined,
        { shallow: true },
      );
    },
  });

  addClientDisclosure = new DisclosureState();
  addPartnerDisclosure = new DisclosureState();
  addInternalUserDisclosure = new DisclosureState();

  task = new ValueState<TaskModel | null>(null);
  taskStatuses = new ValueState<TaskQuery["taskStatuses"]>([]);
  taskPriorities = new ValueState<TaskQuery["taskPriorities"]>([]);
  activeTabId = new ValueState<TaskTabIdEnum>(TaskTabIdEnum.Description);
  taskId = new ValueState<string | undefined>(undefined);
  attachmentsCollapsed = new BooleanState();
  isLoading = new BooleanState();
  isLoadingDuplicate = new BooleanState();
  @observable priorityOptions: IOption[] = [];
  @observable.ref renderMode: RenderModeType = "drawer";

  @observable.ref leftPanelWidth: number = 50; // Default 50%
  @observable.ref leftModalPanelRef = createRef<HTMLDivElement>();
  isDragging = new BooleanState();

  deleteModal = new DisclosureState();
  editTaskModal = new DisclosureState<{ moveMode: boolean }>();

  commentsContainerRef: React.RefObject<HTMLDivElement> = createRef();
  drawerBodyRef = createRef<HTMLDivElement>();

  @observable newlyCreatedCommentID?: TaskComment["id"] = undefined;
  seenLastComment = new BooleanState();
  needsRevision = new BooleanState();

  @observable descriptionLoading = new BooleanState(false);

  descriptionState = new UnionState<DescriptionState>("read");
  @observable.ref latestDescription = EMPTY_EDITOR_CONTENT;

  @observable.ref
  descriptionChanged = false;

  @observable.ref
  commentChanged = false;

  @observable.ref newTabId: TaskTabIdEnum | undefined = undefined;
  discardModal = new DisclosureState();

  communicationTabParentStore: CommunicationTabParentStore;

  internalUserTaskFetcher = new FetchHelper<TaskQuery, TaskQueryVariables>(
    TaskDocument,
  );
  scopedUserTaskFetcher = new FetchHelper<
    TaskForScopedUsersQuery,
    TaskForScopedUsersQueryVariables
  >(TaskForScopedUsersDocument);

  private editWatchersMutator = new MutationHelper<
    EditTaskWatchersMutation,
    EditTaskWatchersMutationVariables
  >(EditTaskWatchersDocument);
  private updateDescriptionMutator = new MutationHelper<
    UpdateTaskDescriptionMutation,
    UpdateTaskDescriptionMutationVariables
  >(UpdateTaskDescriptionDocument);

  @observable.ref
  commentInputValue = "";

  @observable.ref
  descriptionInputValue = "";

  constructor(appStore: AppStore) {
    makeObservable(this);
    this.appStore = appStore;
    this.communicationTabParentStore = new CommunicationTabParentStore(
      appStore,
    );

    this.loadRenderModeFromCache();
    this.loadDividerPositionFromCache();
  }

  @computed
  get amIWatching() {
    if (!this.appStore.authStore.user?.id) return false;

    return this.task.value?.taskWatchersIds.includes(
      this.appStore.authStore.user.id,
    );
  }

  @computed
  get taskWatchersUpdating() {
    return this.editWatchersMutator.isLoading.value;
  }

  @computed
  get descriptionUpdating() {
    return this.updateDescriptionMutator.isLoading.value;
  }

  @computed
  get canStartTrackingTime(): boolean {
    if (!this.disclosureState.isOpen) return false;

    if (
      !this.appStore.workspaceStore.settings?.enable_allocation_meeting_play &&
      this.allocationType === CapacityAllocationTypeEnum.Meeting
    ) {
      return false;
    }

    return can(
      "timeTracking_create_own",
      "timeTracking_create_all",
      "timeTracking_create_team",
    );
  }

  @computed
  get hideTimeTrackingButton() {
    return (allocationType?: CapacityAllocationTypeEnum) =>
      !this.appStore.workspaceStore.settings?.enable_allocation_meeting_play &&
      (allocationType ?? this.allocationType) ===
        CapacityAllocationTypeEnum.Meeting;
  }

  @computed
  get allocationType(): CapacityAllocationTypeEnum | null {
    return this.disclosureState?.additionalData?.allocationType ?? null;
  }

  @action.bound
  async updateWatchers(userIds: TaskWatcher["user_id"][]) {
    if (!Array.isArray(userIds)) {
      this.appStore.UIStore.toast({ status: "error" });
      captureEvent({
        message: "FE: Invalid user IDs for updating task watchers",
      });
      return;
    }

    if (!this.taskId.value) {
      this.appStore.UIStore.toast({ status: "error" });
      captureEvent({
        message: "FE: Missing task ID for updating task watchers",
      });
      return;
    }
    const [data, error] = await this.editWatchersMutator.mutate({
      taskId: this.taskId.value,
      userIds,
    });

    if (error) {
      console.error("Failed to update task watchers", error);
      return;
    }

    this.task.value?.setWatchers(data?.editTaskWatchers.taskWatchers);
  }

  @action.bound
  async updateDescription(
    input: Omit<UpdateTaskDescriptionMutationVariables["input"], "id">,
  ) {
    if (!this.taskId.value || !this.task.value) {
      this.appStore.UIStore.toast({ status: "error" });
      captureEvent({
        message: "FE: Missing task ID for updating task description",
      });
      return;
    }

    const [data, error] = await this.updateDescriptionMutator.mutate({
      input: {
        id: this.taskId.value,
        description: input.description,
        addFiles: input.addFiles,
        removeFiles: input.removeFiles,
      },
    });

    if (error) return;

    this.task.value.setDescription(data?.updateTaskDescription.description);
    this.task.value.revision = data.updateTaskDescription.revision;
    this.latestDescription =
      data?.updateTaskDescription.description?.body ?? EMPTY_EDITOR_CONTENT;
    this.descriptionState.set("read");
    trackEvent("task", "Updated description");
    this.handleChangeDescriptionInputValue(this.latestDescription);
    setTimeout(() => {
      this.setDescriptionChanged(false);
    }, 150);
  }

  @computed get leftPanelWidthPercentage(): string {
    return `${this.leftPanelWidth}%`;
  }

  @computed get rightPanelWidthPercentage(): string {
    return `${100 - this.leftPanelWidth}%`;
  }

  @action.bound
  setNewTabId(tabId: TaskTabIdEnum | undefined) {
    this.newTabId = tabId;
  }

  @action.bound handleChangeTab(index: number) {
    if (
      this.activeTabId.value === TaskTabIdEnum.Communication &&
      this.commentChanged
    ) {
      this.setNewTabId(index);
      this.discardModal.onOpen();
      return;
    }

    const taskTabId = this.taskTabs[index]?.id;
    if (typeof taskTabId !== "number") return;
    this.activeTabId.set(taskTabId);
  }

  @action.bound
  setCommentChanged(changed: boolean) {
    this.commentChanged = changed;
  }

  @action.bound
  setDescriptionChanged(changed: boolean) {
    this.descriptionChanged = changed;
  }

  @action.bound
  loadRenderModeFromCache() {
    try {
      const cachedMode = localStorage.getItem(RENDER_MODE_CACHE_KEY);
      if (cachedMode && (cachedMode === "drawer" || cachedMode === "modal")) {
        this.renderMode = cachedMode;
      }
    } catch (error) {
      console.error("Failed to load render mode from cache", error);
    }
  }

  @action.bound
  loadDividerPositionFromCache() {
    try {
      const cachedPosition = localStorage.getItem(DIVIDER_POSITION_CACHE_KEY);
      if (cachedPosition) {
        const parsedPosition = parseFloat(cachedPosition);
        if (!Number.isNaN(parsedPosition)) {
          this.leftPanelWidth = parsedPosition;
        }
      }
    } catch (error) {
      console.error("Failed to load divider position from cache", error);
    }
  }

  debouncedSaveDividerPosition = debounce((position: number) => {
    try {
      localStorage.setItem(DIVIDER_POSITION_CACHE_KEY, position.toString());
    } catch (error) {
      console.error("Failed to save divider position to cache", error);
    }
  }, 500);

  @action.bound
  saveRenderModeToCache(renderMode: RenderModeType) {
    try {
      localStorage.setItem(RENDER_MODE_CACHE_KEY, renderMode);
    } catch (error) {
      console.error("Failed to save render mode to cache", error);
    }
  }

  @action.bound
  setRenderMode(mode: RenderModeType) {
    this.renderMode = mode;
    this.saveRenderModeToCache(mode);

    if (
      mode === "modal" &&
      this.activeTabId.value === TaskTabIdEnum.Communication
    ) {
      this.activeTabId.set(TaskTabIdEnum.Description);
      return;
    }

    if (!this.commentChanged) return;
    this.activeTabId.set(TaskTabIdEnum.Communication);
  }

  @action.bound
  updateDividerPosition(mouseX: number, containerWidth: number) {
    if (!this.isDragging.value) return;

    // Calculate percentage (clamped between 20% and 80%)
    const newLeftWidthPercent = Math.min(
      Math.max((mouseX / containerWidth) * 100, 20),
      80,
    );

    this.leftPanelWidth = newLeftWidthPercent;
    this.debouncedSaveDividerPosition(newLeftWidthPercent);
  }

  // Mouse event handlers
  @action.bound
  handleDividerMouseDown() {
    this.isDragging.on();
  }

  @action.bound
  handleContainerMouseMove(
    e: React.MouseEvent,
    containerRef: React.RefObject<HTMLDivElement>,
  ) {
    if (!this.isDragging.value || !containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const mouseX = e.clientX - containerRect.left;

    this.updateDividerPosition(mouseX, containerWidth);
  }

  @action.bound
  handleContainerMouseUp() {
    this.isDragging.off();
  }

  @action.bound
  handleContainerMouseLeave() {
    this.isDragging.off();
  }

  @computed
  get isDrawerMode(): boolean {
    return this.renderMode === "drawer";
  }

  @computed get isInternalUser(): boolean {
    return this.appStore.authStore.isInternalUser;
  }

  @computed get statusOptions() {
    return this.taskStatuses.value.map((status) => ({
      label: status.name,
      value: status.id,
    }));
  }

  @computed get showTaskInfo() {
    const budgetItem = this.task.value?.ourWorkBudgetItem;
    return {
      duration: !!this.task.value?.from || !!this.task.value?.to,
      deadline: !!this.task.value?.deadline,
      tracked: this.isInternalUser && budgetItem,
      budget: this.isInternalUser && budgetItem,
    };
  }

  @computed get taskInfoItemsCount(): number {
    return Object.values(this.showTaskInfo).reduce((prev, curr) => {
      if (!curr) return prev;
      return prev + 1;
      // NOTE: 1 because we always show priority
    }, 1);
  }

  @computed get taskInfoColumnCount(): number {
    return this.taskInfoItemsCount >= 3 ? 3 : this.taskInfoItemsCount;
  }

  @computed get addTaskInfoSpacer(): boolean {
    return this.taskInfoItemsCount > 3;
  }

  @computed get taskInfoSpacerSpan(): number {
    return 6 - this.taskInfoItemsCount;
  }

  @computed get clientIds() {
    return this.task.value?.clients.map((client) => client.id) ?? [];
  }

  @computed get partnerIds() {
    return this.task.value?.partners.map((partner) => partner.id) ?? [];
  }

  @computed
  get isOvertime() {
    return (spentTime?: string | number, plannedTime?: Maybe<number>) =>
      !!this.appStore.workspaceStore.settings?.highlight_overtime_in_tasks &&
      spentTime !== undefined &&
      typeof plannedTime === "number" &&
      Number(spentTime) > plannedTime;
  }

  @action.bound async fetchTask(
    { silently }: { silently?: boolean } = { silently: false },
  ) {
    if (!this.taskId.value) return;
    if (!silently) this.isLoading.on();

    let data: TaskQuery | TaskForScopedUsersQuery | null = null;
    let error: Error | readonly GraphQLFormattedError[] | null = null;

    if (this.isInternalUser) {
      const [queryData, queryError] = await this.internalUserTaskFetcher.fetch(
        {
          id: this.taskId.value,
        },
        undefined,
        true,
      );
      data = queryData;
      error = queryError;
    } else {
      const [queryData, queryError] = await this.scopedUserTaskFetcher.fetch(
        {
          id: this.taskId.value,
        },
        undefined,
        true,
      );
      data = queryData;
      error = queryError;
    }

    if (error || !data) {
      const isTaskDeleted =
        Array.isArray(error) &&
        error.some(
          (e) =>
            "message" in e &&
            e.message === "Validation failed for the field [taskPositionStat].",
        );

      if (isTaskDeleted) {
        this.appStore.UIStore.toast({
          status: "error",
          title: t`This task was deleted.`,
        });
      } else {
        Array.isArray(error) &&
          error.forEach((error) => {
            this.appStore.UIStore.toast({
              status: "error",
              title: error?.message ?? undefined,
            });
          });
      }

      this.disclosureState.onClose();
      this.isLoading.off();
      return;
    }

    this.task.set(
      new TaskModel(
        data.task,
        "taskPositionStat" in data ? data.taskPositionStat : undefined,
      ),
    );
    this.taskStatuses.set(data.taskStatuses);
    this.taskPriorities.set(data.taskPriorities);
    this.latestDescription =
      this.task.value?.description?.body ?? EMPTY_EDITOR_CONTENT;
    this.needsRevision.off();
    this.handleChangeDescriptionInputValue(this.latestDescription);

    if (!silently) this.isLoading.off();
  }

  @action.bound
  refetchMentionsUsers(): void {
    const taskId = this.taskId.value;
    if (!taskId) return;

    this.appStore.workspaceStore.invalidateMentionsCache(taskId);

    this.appStore.workspaceStore.getDefaultMentionsUsers(["internal"], taskId);
    for (const tab of this.communicationTabParentStore.availableTabs) {
      this.appStore.workspaceStore.getDefaultMentionsUsers(
        tab.userTypesWithAccess,
        taskId,
      );
    }
  }

  @computed
  get currentTabIndex() {
    return this.taskTabs.findIndex((tab) => tab.id === this.activeTabId.value);
  }

  @computed
  get taskTabs(): TTaskTab[] {
    return (
      [
        {
          id: TaskTabIdEnum.Description,
          label: () => t`Description`,
          icon: "file-02",
          component: <DescriptionTab />,
          userTypesWithAccess: ["internal", "client", "partner"],
        },
        this.isDrawerMode && {
          id: TaskTabIdEnum.Communication,
          label: () => t`Communication`,
          icon: "message-chat-square",
          component: <CommunicationTabParent />,
          userTypesWithAccess: ["internal", "client", "partner"],
        },
        {
          id: TaskTabIdEnum.Attachments,
          label: () => t`Files`,
          icon: "paperclip",
          component: <TaskAttachmentsTab />,
          userTypesWithAccess: ["internal", "client", "partner"],
        },
        {
          id: TaskTabIdEnum.LinkedTasks,
          label: () => t`Linked tasks`,
          icon: "check-done-01",
          component: <LinkedTasksTab />,
          userTypesWithAccess: ["internal"],
        },
        {
          id: TaskTabIdEnum.Subtasks,
          label: () => t`Subtasks`,
          icon: "git-branch-01",
          component: <SubtasksTab />,
          userTypesWithAccess: ["internal"],
        },
      ] as TTaskTab[]
    )
      .filter(Boolean)
      .filter((tab) =>
        tab.userTypesWithAccess.includes(this.appStore.authStore.user?.type!),
      )
      .filter((tab) => {
        if (tab.id !== TaskTabIdEnum.Subtasks) return true;
        return !this.task.value?.parent;
      });
  }

  createAttachmentFromFile(
    file: Pick<AssignableFile, "public_id" | "filename" | "mime_type" | "urls">,
  ): Attachment {
    return {
      id: file.public_id,
      filename: file.filename,
      mime_type: file.mime_type,
      viewable: file.urls.viewable ?? undefined,
      original: file.urls.original ?? undefined,
      thumbnail: file.urls.thumbnail ?? undefined,
    };
  }

  @action.bound
  handleChangeCommentInputValue(value: string) {
    this.commentInputValue = value;
  }

  @action.bound
  handleChangeDescriptionInputValue(value: string) {
    this.descriptionInputValue = value;
  }

  @action.bound
  private handleBeforeUnload = (event: BeforeUnloadEvent) => {
    if (!this.commentChanged && !this.descriptionChanged) return;

    const message = t`You have unsaved changes. Are you sure you want to leave the page?`;
    event.preventDefault();
    event.returnValue = message;
    return message;
  };

  @action.bound
  private addBeforeUnloadListener() {
    window.addEventListener("beforeunload", this.handleBeforeUnload);
  }

  @action.bound
  private removeBeforeUnloadListener() {
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
  }

  @computed
  get isStarLoading() {
    if (!this.task.value?.id) {
      return false;
    }

    return this.appStore.starStore.isLoading(
      this.task.value.id,
      StarsStarableTypeEnum.Task,
    );
  }

  @action.bound
  async toggleStar() {
    if (!this.task.value?.id) {
      return;
    }

    if (this.task.value.is_starred) {
      const [_, error] = await this.appStore.starStore.unstar(
        this.task.value.id,
        StarsStarableTypeEnum.Task,
      );

      if (error) return;

      this.task.value.is_starred = false;

      this.appStore.UIStore.toast({
        title: t`Task unstarred`,
        status: "success",
      });
    } else {
      const [_, error] = await this.appStore.starStore.star(
        this.task.value.id,
        StarsStarableTypeEnum.Task,
      );

      if (error) return;

      this.task.value.is_starred = true;

      this.appStore.UIStore.toast({
        title: t`Task starred`,
        status: "success",
      });
    }
  }
}
